import { useState, useRef, useCallback, useEffect } from 'react';
import { useSession } from '@clerk/clerk-react';
import { X, ArrowRight, Sparkle, Lightbulb, Buildings, Cube } from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import { API_URL } from 'constants/index';
import { ScenarioManager } from './ScenarioManager';
import { ScenarioResult, AssistantContext } from './types';
import { AnalyticsVisualization } from '../AnalyticsVisualization';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';

interface UniversalAiAssistantProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Universal AI Assistant
 *
 * Eén chat interface die automatisch detecteert welk scenario van toepassing is:
 * - Ticket aanmaken
 * - Ticket analytics
 * - Building analytics
 * - Financial analytics (toekomstig)
 * - etc.
 *
 * Geen tabs - intelligente scenario detectie
 */
export const UniversalAiAssistant: React.FC<UniversalAiAssistantProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { session } = useSession();
  const scenarioManager = useRef(new ScenarioManager()).current;

  // State
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ScenarioResult | null>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [aiChatResponse, setAiChatResponse] = useState<string | null>(null);
  const [currentTicketId, setCurrentTicketId] = useState<number | null>(null);

  // File search context state
  const [fileSearchContext, setFileSearchContext] = useState<{
    hasContext: boolean;
    lastBuildingId?: string;
    lastUnitId?: string;
    lastLabel?: string;
  } | null>(null);

  // @-mention functionaliteit met keyboard navigation
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionSuggestions, setMentionSuggestions] = useState<Array<{
    id: string;
    type: 'building' | 'unit';
    googlePlaceLongAddress?: string;
    companyName?: string;
    boxNumber?: string;
    buildingId?: string;
    building?: { id: string; companyName?: string };
    matchScore: number;
  }>>([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Fetch buildings AND units based on search query
   */
  const searchBuildingsAndUnits = useCallback(async (query: string) => {
    console.log('[Mentions] searchBuildingsAndUnits called with query:', query);
    if (!session) {
      console.log('[Mentions] No session available, skipping search');
      return;
    }

    try {
      // Fetch both buildings and units in parallel
      const [buildingsResponse, unitsResponse] = await Promise.all([
        fetchWithAuth(
          `${API_URL}/helpdesk/buildings/fuzzy-search?q=${encodeURIComponent(query)}`,
          { method: 'GET' },
          session
        ),
        fetchWithAuth(
          `${API_URL}/unit/fuzzy-search?q=${encodeURIComponent(query)}`,
          { method: 'GET' },
          session
        )
      ]);

      const buildings = buildingsResponse.ok ? await buildingsResponse.json() : [];
      const units = unitsResponse.ok ? await unitsResponse.json() : [];

      console.log('[Mentions] Buildings received:', buildings.length, buildings);
      console.log('[Mentions] Units received:', units.length, units);

      // Tag buildings and units with type
      const taggedBuildings = buildings.map((b: any) => ({ ...b, type: 'building' as const }));
      const taggedUnits = units.map((u: any) => ({ ...u, type: 'unit' as const }));

      // Combine and sort by matchScore (highest first)
      const combined = [...taggedBuildings, ...taggedUnits]
        .sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0))
        .slice(0, 10); // Limit to 10 total suggestions

      console.log('[Mentions] Combined suggestions:', combined);
      setMentionSuggestions(combined);
    } catch (error) {
      console.error('[Mentions] Error searching buildings and units:', error);
    }
  }, [session]);

  /**
   * Fetch file search context on mount
   */
  useEffect(() => {
    const fetchFileContext = async () => {
      if (!session?.user?.id) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/ai/context/${session.user.id}`,
          { method: 'GET' },
          session
        );

        if (response.ok) {
          const data = await response.json();
          if (data.hasContext) {
            setFileSearchContext({
              hasContext: true,
              lastBuildingId: data.context.lastBuildingId,
              lastUnitId: data.context.lastUnitId,
              lastLabel: data.context.lastLabel,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching file context:', error);
      }
    };

    if (isOpen) {
      fetchFileContext();
    }
  }, [isOpen, session]);

  /**
   * Detecteer @ in input voor autocomplete
   */
  const checkForMentions = useCallback((text: string, cursor: number) => {
    console.log('[Mentions] Checking for mentions:', { text, cursor });
    const textBeforeCursor = text.substring(0, cursor);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    console.log('[Mentions] Last @ index:', lastAtIndex, 'Text before cursor:', textBeforeCursor);

    if (lastAtIndex >= 0) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      console.log('[Mentions] Text after @:', textAfterAt);
      // Check if there's no space after @
      if (!textAfterAt.includes(' ')) {
        const query = textAfterAt;
        console.log('[Mentions] Opening dropdown with query:', query);
        setMentionQuery(query);
        setMentionStartPos(lastAtIndex);
        setShowMentionDropdown(true);
        setSelectedMentionIndex(0);

        // Fetch buildings and units if query has length
        if (query.length > 0) {
          console.log('[Mentions] Fetching buildings and units with query:', query);
          searchBuildingsAndUnits(query);
        } else {
          // Show all buildings and units if just typed @
          console.log('[Mentions] Fetching all buildings and units');
          searchBuildingsAndUnits('');
        }
        return;
      }
    }

    console.log('[Mentions] Closing dropdown');
    setShowMentionDropdown(false);
    setMentionQuery('');
  }, [searchBuildingsAndUnits]);

  /**
   * Handle building selection via @-mention
   */
  const selectMention = useCallback((building: { id: string }) => {
    const beforeMention = input.substring(0, mentionStartPos);
    const afterMention = input.substring(mentionStartPos + mentionQuery.length + 1);
    const newText = `${beforeMention}@${building.id} ${afterMention}`;

    setInput(newText);
    setShowMentionDropdown(false);
    setMentionQuery('');

    // Focus and restore cursor position
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPos = beforeMention.length + building.id.length + 2;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [input, mentionStartPos, mentionQuery]);

  /**
   * Handle keyboard navigation in mention dropdown
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showMentionDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMentionIndex(prev =>
        prev < mentionSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : prev);
    } else if (e.key === 'Enter' && mentionSuggestions.length > 0) {
      e.preventDefault();
      selectMention(mentionSuggestions[selectedMentionIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowMentionDropdown(false);
      setMentionQuery('');
    }
  }, [showMentionDropdown, mentionSuggestions, selectedMentionIndex, selectMention]);

  /**
   * Process user input
   */
  const handleProcess = async () => {
    if (!input.trim()) {
      toast.error('Voer een vraag of opdracht in');
      return;
    }

    // Check for /clear command to reset file search context
    if (input.trim().toLowerCase() === '/clear') {
      if (!session?.user?.id) {
        toast.error('Geen gebruikerssessie beschikbaar');
        setInput('');
        return;
      }

      setIsProcessing(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/ai/clear-context`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: session.user.id }),
          },
          session
        );

        if (response.ok) {
          toast.success('Context gewist! Je kunt nu een nieuwe zoekopdracht starten.');
          setInput('');
          setResult(null);
          setChartData(null);
          setAiChatResponse(null);
          setFileSearchContext(null); // Clear context indicator
        } else {
          toast.error('Fout bij wissen van context');
        }
      } catch (error) {
        console.error('Error clearing context:', error);
        toast.error('Fout bij wissen van context');
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    setIsProcessing(true);
    setResult(null);
    setChartData(null);

    try {
      // Detect best matching scenario
      const { handler, detection } = scenarioManager.detectScenario(input);

      if (detection.confidence < 0.3) {
        toast.warning('Ik begrijp je vraag niet helemaal. Probeer een voorbeeld query.');
        setIsProcessing(false);
        return;
      }

      const context: AssistantContext = {
        session,
        apiUrl: API_URL,
        onSuccess,
        onError: (error) => {
          console.error('Error:', error);
          toast.error(error.message);
        }
      };

      // Parse input using the detected scenario
      const parsedResult = await handler.parse(input, context);
      setResult(parsedResult);

      // If it's an action that needs preview (like create), show preview
      if (parsedResult.action === 'create' && parsedResult.preview) {
        // Just show preview, don't execute yet
        setIsProcessing(false);
        return;
      }

      // Execute immediately for queries/analyze
      if (parsedResult.action === 'analyze' || parsedResult.action === 'query') {
        const data = await handler.execute(parsedResult, context);
        console.log('Received analytics data:', data);
        setChartData(data);
      }
    } catch (error: any) {
      console.error('Error processing input:', error);
      toast.error(error.message || 'Fout bij verwerken van je vraag');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Execute the action (for create operations after preview)
   */
  const handleExecute = async () => {
    if (!result) return;

    setIsProcessing(true);

    try {
      const { handler } = scenarioManager.detectScenario(input);

      const context: AssistantContext = {
        session,
        apiUrl: API_URL,
        onSuccess,
      };

      const data = await handler.execute(result, context);

      if (result.action === 'create') {
        toast.success('Succesvol aangemaakt!');
        onSuccess?.();
        handleReset();
      } else {
        setChartData(data);
      }
    } catch (error: any) {
      console.error('Error executing action:', error);
      toast.error(error.message || 'Fout bij uitvoeren van actie');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Reset form
   */
  const handleReset = () => {
    setInput('');
    setResult(null);
    setChartData(null);
    setAiChatResponse(null);
    setCurrentTicketId(null);
  };

  /**
   * Use example query
   */
  const handleExampleClick = (example: string) => {
    setInput(example);
  };

  /**
   * Analyze specific ticket using AI chat endpoint
   */
  const handleAnalyzeTicket = useCallback(async (ticketId: number) => {
    if (!session) {
      toast.error('Geen sessie beschikbaar');
      return;
    }

    const question = `Analyseer dit ticket uitgebreid. Wat is het probleem? Welke acties zijn ondernomen? Wat zijn de next steps?`;
    setInput(`Ticket H${ticketId} analyseren...`);
    setCurrentTicketId(ticketId);

    setIsProcessing(true);
    setResult(null);
    setChartData(null);
    setAiChatResponse(null);

    try {
      console.log(`🤖 Calling AI chat endpoint for ticket H${ticketId}`);

      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ai/chat-ticket/${ticketId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ question }),
        },
        session
      );

      if (!response.ok) {
        throw new Error('AI chat request failed');
      }

      const data = await response.json();
      console.log('🎉 AI chat response:', data);

      // Store the AI response
      setAiChatResponse(data.answer);
      toast.success('Ticket analyse compleet!');
    } catch (error: any) {
      console.error('❌ Error calling AI chat:', error);
      toast.error(error.message || 'Fout bij analyseren van ticket met AI');
    } finally {
      setIsProcessing(false);
    }
  }, [session]);

  /**
   * Get all examples grouped by scenario
   */
  const examplesByScenario = scenarioManager.getAllExamples();

  // Render nothing if not open
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200">
      <div className="w-full max-w-4xl rounded-xl bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 ease-out max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 bg-gradient-to-r from-moss-primary to-moss-primary/80 border-b border-moss-primary/20">
          <div className="flex items-center gap-3">
            <Sparkle className="w-6 h-6 text-white" weight="fill" />
            <h3 className="text-lg font-semibold text-white">AI Assistent</h3>
            {result && (
              <span className="text-xs text-white/80 px-2 py-1 bg-white/20 rounded-full">
                {result.scenario}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
            disabled={isProcessing}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Info with examples */}
          <div className="text-sm text-neutral-primary bg-blue-50 border border-blue-200 p-3 rounded-lg">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" weight="fill" />
              <div className="flex-1">
                <strong>💡 Stel een vraag in gewone taal</strong>
                <div className="mt-1 text-xs text-neutral-secondaryMid space-y-1">
                  <p>Ik begrijp natuurlijke vragen zoals "Hoeveel tickets heb ik?" of "Waar heb ik de meeste problemen?"</p>
                  <p className="flex items-center gap-1">
                    <span className="font-semibold text-brick-primary">@</span>
                    <span>Typ @ gevolgd door een gebouw of unit ID voor suggesties (bijv: @EMIE-12 of @EMIE-12-1)</span>
                  </p>
                </div>

                {/* Examples */}
                <div className="mt-3 space-y-2">
                  {Array.from(examplesByScenario.entries()).map(([scenario, examples]) => (
                    <div key={scenario}>
                      <p className="text-xs font-semibold text-neutral-primary mb-1">{scenario}:</p>
                      <div className="flex flex-wrap gap-1">
                        {examples.slice(0, 4).map((example, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleExampleClick(example)}
                            className="text-xs px-2 py-1 bg-white border border-blue-200 rounded-md hover:bg-blue-50 transition-colors"
                            disabled={isProcessing}
                          >
                            {example}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* File Search Context Indicator */}
          {fileSearchContext?.hasContext && (
            <div className="mb-3 px-3 py-2 bg-moss-primary/10 border border-moss-primary/20 rounded-md">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-moss-primary font-medium">📋 Context:</span>
                  <div className="flex items-center gap-2 text-neutral-primary">
                    {fileSearchContext.lastBuildingId && (
                      <span className="bg-white px-2 py-0.5 rounded border border-moss-primary/20">
                        @{fileSearchContext.lastBuildingId}
                      </span>
                    )}
                    {fileSearchContext.lastLabel && (
                      <span className="text-neutral-secondaryMid">• {fileSearchContext.lastLabel}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setInput('/clear')}
                  className="text-xs text-moss-primary hover:text-moss-primary/70 underline"
                >
                  Wissen
                </button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="relative">
            <label className="block text-sm font-medium text-neutral-primary mb-2">
              Wat wil je weten of doen?
              {fileSearchContext?.hasContext && (
                <span className="text-xs text-neutral-secondaryMid ml-2">
                  (Type /clear om context te resetten)
                </span>
              )}
            </label>
            <textarea
              ref={textareaRef}
              className="w-full border border-neutral-primary/20 rounded-md px-3 py-2 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-moss-primary/20 resize-none"
              placeholder="Stel een vraag of geef een opdracht in gewone taal... Gebruik @ voor gebouw/unit suggesties."
              value={input}
              onChange={(e) => {
                const newValue = e.target.value;
                const cursor = e.target.selectionStart || 0;
                setInput(newValue);
                checkForMentions(newValue, cursor);
              }}
              onKeyDown={handleKeyDown}
              onKeyUp={(e) => {
                const currentValue = e.currentTarget.value;
                const cursor = e.currentTarget.selectionStart || 0;
                checkForMentions(currentValue, cursor);
              }}
              onClick={(e) => {
                const currentValue = e.currentTarget.value;
                const cursor = e.currentTarget.selectionStart || 0;
                checkForMentions(currentValue, cursor);
              }}
              disabled={isProcessing}
            />

            {/* Building mentions dropdown */}
            {(() => {
              console.log('[Mentions] Dropdown render check:', {
                showMentionDropdown,
                suggestionsLength: mentionSuggestions.length,
                mentionQuery,
                suggestions: mentionSuggestions
              });
              return null;
            })()}
            {showMentionDropdown && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-neutral-primary/20 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {mentionSuggestions.length === 0 && (
                  <div className="px-4 py-3 text-sm text-neutral-secondaryMid">
                    Zoeken naar gebouwen en units...
                  </div>
                )}
                {mentionSuggestions.length > 0 && (
                  <>
                {mentionQuery && (
                  <div className="px-4 py-2 text-xs text-neutral-secondaryMid border-b border-neutral-primary/10">
                    Zoeken naar: <span className="font-semibold text-neutral-primary">{mentionQuery}</span>
                  </div>
                )}
                {mentionSuggestions.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => selectMention(item)}
                    className={`w-full px-4 py-2 text-left hover:bg-moss-primary/10 transition-colors flex items-center gap-3 ${
                      index === selectedMentionIndex ? 'bg-moss-primary/10' : ''
                    }`}
                  >
                    {item.type === 'building' ? (
                      <Buildings className="w-4 h-4 text-moss-primary flex-shrink-0" weight="fill" />
                    ) : (
                      <Cube className="w-4 h-4 text-brick-primary flex-shrink-0" weight="fill" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-primary truncate">
                        {item.id}
                      </div>
                      {item.type === 'building' && item.companyName && (
                        <div className="text-xs text-moss-primary truncate">
                          {item.companyName}
                        </div>
                      )}
                      {item.type === 'building' && item.googlePlaceLongAddress && (
                        <div className="text-xs text-neutral-secondaryMid truncate">
                          {item.googlePlaceLongAddress}
                        </div>
                      )}
                      {item.type === 'unit' && (
                        <div className="text-xs text-brick-primary truncate">
                          {item.boxNumber ? `Box: ${item.boxNumber}` : 'Unit'} • {item.building?.companyName || item.buildingId || 'Gebouw'}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Process Button */}
          {!result && !chartData && (
            <button
              onClick={handleProcess}
              disabled={!input.trim() || isProcessing}
              className="w-full px-4 py-2 rounded-md bg-moss-primary text-white font-medium transition-colors hover:bg-moss-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessing ? 'Verwerken...' : 'Verwerken'}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}

          {/* Preview (for create actions) */}
          {result && result.action === 'create' && result.preview && !chartData && (
            <div className="border border-moss-primary/30 rounded-lg p-4 bg-moss-primary/5 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-moss-primary">
                <Sparkle className="w-4 h-4" weight="fill" />
                <span>Preview - {result.scenario}</span>
              </div>

              <div className="space-y-2 text-sm">
                {Object.entries(result.preview).map(([key, value]) => (
                  <div key={key} className="flex gap-2">
                    <span className="font-medium text-neutral-secondaryMid min-w-[100px] capitalize">
                      {key}:
                    </span>
                    <span className="text-neutral-primary">{String(value)}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleReset}
                  disabled={isProcessing}
                  className="flex-1 px-3 py-2 text-sm border border-neutral-primary/20 rounded-md hover:bg-neutral-primary/5 disabled:opacity-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleExecute}
                  disabled={isProcessing}
                  className="flex-1 px-3 py-2 text-sm bg-moss-primary text-white rounded-md hover:bg-moss-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isProcessing ? 'Uitvoeren...' : 'Uitvoeren'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* AI Chat Response */}
          {aiChatResponse && currentTicketId && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-moss-primary/5 to-brick-primary/5 border border-moss-primary/30 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkle className="w-5 h-5 text-moss-primary" weight="fill" />
                  <h4 className="text-base font-semibold text-neutral-primary">
                    AI Analyse: Ticket H{currentTicketId}
                  </h4>
                </div>
                <div className="prose prose-sm max-w-none">
                  <div className="text-sm text-neutral-primary whitespace-pre-wrap leading-relaxed">
                    {aiChatResponse}
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => window.open(`/helpdesk/${currentTicketId}`, '_blank')}
                  className="flex-1 px-4 py-2 text-sm bg-moss-primary text-white rounded-md hover:bg-moss-primary/90 transition-colors"
                >
                  Open ticket H{currentTicketId}
                </button>
                <button
                  onClick={handleReset}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 text-sm border border-neutral-primary/20 rounded-md hover:bg-neutral-primary/5 transition-colors"
                >
                  Nieuwe vraag stellen
                </button>
              </div>
            </div>
          )}

          {/* Chart Results */}
          {chartData && (
            <div className="space-y-4">
              <AnalyticsVisualization
                analyticsData={chartData}
                onAnalyzeTicket={handleAnalyzeTicket}
              />

              <button
                onClick={handleReset}
                disabled={isProcessing}
                className="w-full px-4 py-2 text-sm border border-neutral-primary/20 rounded-md hover:bg-neutral-primary/5 transition-colors"
              >
                Nieuwe vraag stellen
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
